// Salida anticipada de un circuito: el usuario se arrepiente en medio del
// camino (login → plan → pago, alta, cambio de contraseña…) y vuelve a la app
// que lo mandó, avisándole que la acción NO se completó.
//
// No inventamos un contrato nuevo: cada circuito ya tenía su forma de devolver
// al usuario cuando algo salía mal, y reusamos esa.
//
//   - Circuito OAuth (`/authorize` → login/signup/pay): el estándar es el error
//     redirect de RFC 6749 §4.1.2.1 — `?error=access_denied&error_description=…`
//     con el `state` de vuelta. `access_denied` es literalmente "el dueño del
//     recurso denegó la solicitud", que es exactamente esto. El backend ya lo
//     usa para sus propios rechazos (buildRedirectWithError en el handler de
//     authorize), así que la app del partner ya sabe leerlo.
//
//   - Circuito con `return_url` (subscribe, cambio de contraseña): el contrato
//     es `?state&status=success|cancelled|failed`, el mismo que usa la pantalla
//     al terminar bien. Salir a mitad de camino es `cancelled`.
//
// Ojo con el `return_url`: la pantalla que lo recibe debe haber validado antes
// que esté en la whitelist de `redirect_uris` del client (`return_url_ok`).
// Este módulo arma la URL, no la autoriza.

export type FlowExit =
  | { kind: 'oauth'; redirectUri: string; state?: string | null }
  | { kind: 'return_url'; returnUrl: string; state?: string | null };

/** Lo que ve el partner en `error_description`. En inglés, como el resto del contrato OAuth. */
export const EXIT_DESCRIPTION = 'user left the flow before completing it';

/**
 * URL a la que volver, o `null` si no hay destino utilizable (sin URL, o
 * malformada). El caller que reciba `null` NO debe ofrecer la salida: no
 * tenemos a dónde mandarlo y un botón que no hace nada es peor que no tenerlo.
 */
export function buildFlowExitUrl(exit: FlowExit): string | null {
  const raw = exit.kind === 'oauth' ? exit.redirectUri : exit.returnUrl;
  if (!raw) return null;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  if (exit.kind === 'oauth') {
    url.searchParams.set('error', 'access_denied');
    url.searchParams.set('error_description', EXIT_DESCRIPTION);
  } else {
    url.searchParams.set('status', 'cancelled');
  }
  if (exit.state) url.searchParams.set('state', exit.state);
  return url.toString();
}
